import {
  infiniteQueryOptions,
  queryOptions,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query'
import { apiFetch } from '@shared/api/http'
import { toQueryString } from '@shared/api/query'
import {
  WebhookCatalogSchema,
  WebhookDeliveriesPageSchema,
  WebhookDeliveryDetailSchema,
  WebhookSubscriptionListSchema,
  WebhookSubscriptionSchema,
  type WebhookCatalogItem,
  type WebhookDeliveriesPage,
  type WebhookDeliveryDetail,
  type WebhookDeliveryStatus,
  type WebhookSubscription,
} from '@shared/contracts/webhooks'

export type WebhookDeliveryFilters = {
  status?: WebhookDeliveryStatus[]
  limit?: number
}

export const webhookKeys = {
  all: ['webhooks'] as const,
  catalog: () => [...webhookKeys.all, 'catalog'] as const,
  lists: () => [...webhookKeys.all, 'list'] as const,
  detail: (webhookId: string) => [...webhookKeys.all, 'detail', webhookId] as const,
  deliveries: (webhookId: string, filters?: WebhookDeliveryFilters) =>
    filters
      ? ([...webhookKeys.detail(webhookId), 'deliveries', filters] as const)
      : ([...webhookKeys.detail(webhookId), 'deliveries'] as const),
  deliveryDetail: (webhookId: string, deliveryId: string) =>
    [...webhookKeys.detail(webhookId), 'deliveries', deliveryId] as const,
}

export async function getWebhookCatalog(): Promise<WebhookCatalogItem[]> {
  return WebhookCatalogSchema.parse(await apiFetch<unknown>('/webhooks/catalog'))
}

export function webhookCatalogQuery() {
  return queryOptions({
    queryKey: webhookKeys.catalog(),
    queryFn: getWebhookCatalog,
  })
}

export function useWebhookCatalogQuery() {
  return useQuery(webhookCatalogQuery())
}

export async function getWebhooks(): Promise<WebhookSubscription[]> {
  return WebhookSubscriptionListSchema.parse(await apiFetch<unknown>('/webhooks'))
}

export function webhooksQuery() {
  return queryOptions({
    queryKey: webhookKeys.lists(),
    queryFn: getWebhooks,
  })
}

export function useWebhooksQuery() {
  return useQuery(webhooksQuery())
}

export async function getWebhook(webhookId: string): Promise<WebhookSubscription> {
  return WebhookSubscriptionSchema.parse(await apiFetch<unknown>(`/webhooks/${webhookId}`))
}

export function webhookQuery(webhookId: string) {
  return queryOptions({
    queryKey: webhookKeys.detail(webhookId),
    queryFn: () => getWebhook(webhookId),
    enabled: Boolean(webhookId),
  })
}

export function useWebhookQuery(webhookId: string | undefined) {
  return useQuery({
    ...webhookQuery(webhookId ?? ''),
    enabled: Boolean(webhookId),
  })
}

export async function getWebhookDeliveriesPage(
  webhookId: string,
  params: WebhookDeliveryFilters & { cursor?: string | null },
): Promise<WebhookDeliveriesPage> {
  const query = toQueryString({
    cursor: params.cursor ?? undefined,
    limit: params.limit,
    status: params.status,
  })
  return WebhookDeliveriesPageSchema.parse(
    await apiFetch<unknown>(`/webhooks/${webhookId}/deliveries${query}`),
  )
}

export function webhookDeliveriesInfiniteQuery(
  webhookId: string,
  filters: WebhookDeliveryFilters = {},
) {
  return infiniteQueryOptions({
    queryKey: webhookKeys.deliveries(webhookId, filters),
    queryFn: ({ pageParam }) =>
      getWebhookDeliveriesPage(webhookId, { ...filters, cursor: pageParam ?? undefined }),
    enabled: Boolean(webhookId),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage: WebhookDeliveriesPage) => lastPage.nextCursor,
  })
}

export function useWebhookDeliveriesInfiniteQuery(
  webhookId: string | undefined,
  filters: WebhookDeliveryFilters = {},
) {
  return useInfiniteQuery({
    ...webhookDeliveriesInfiniteQuery(webhookId ?? '', filters),
    enabled: Boolean(webhookId),
  })
}

export async function getWebhookDeliveryDetail(
  webhookId: string,
  deliveryId: string,
): Promise<WebhookDeliveryDetail> {
  return WebhookDeliveryDetailSchema.parse(
    await apiFetch<unknown>(`/webhooks/${webhookId}/deliveries/${deliveryId}`),
  )
}

export function webhookDeliveryDetailQuery(webhookId: string, deliveryId: string) {
  return queryOptions({
    queryKey: webhookKeys.deliveryDetail(webhookId, deliveryId),
    queryFn: () => getWebhookDeliveryDetail(webhookId, deliveryId),
    enabled: Boolean(webhookId && deliveryId),
  })
}
